import { useEffect, useId, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  CodeField,
  Dialog,
  ErrorSummary,
  Field as FormField,
  FormLayout,
  FormLayoutRow,
  Input,
  Select,
} from '../common'
import styles from './ResourceConfigReplacementModal.module.css'
import {
  getDefaultResourceConfig,
  normalizeResourceConfig,
  parseRawResourceConfig,
  validateResourceConfig,
} from './resourceConfigSchema'

export function ResourceConfigReplacementModal({
  open,
  resourceType,
  onClose,
  onSubmit,
  isSubmitting = false,
}) {
  const formId = useId()
  const errorSummaryRef = useRef(null)
  const [draft, setDraft] = useState(() => getDefaultResourceConfig(resourceType))
  const [errors, setErrors] = useState({})
  const [advanced, setAdvanced] = useState(false)
  const [rawConfig, setRawConfig] = useState('{}')

  useEffect(() => {
    if (!open) return
    const defaults = getDefaultResourceConfig(resourceType)
    setDraft(defaults)
    setRawConfig(JSON.stringify(defaults, null, 2))
    setErrors({})
    setAdvanced(false)
  }, [open, resourceType])

  const showErrors = (nextErrors) => {
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) requestAnimationFrame(() => errorSummaryRef.current?.focus())
    return Object.keys(nextErrors).length > 0
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (advanced) {
      const parsed = parseRawResourceConfig(rawConfig)
      if (parsed.error) {
        showErrors({ raw: parsed.error })
        return
      }
      if (showErrors(validateResourceConfig(resourceType, parsed.config))) return
      await onSubmit(parsed.config)
      return
    }

    if (showErrors(validateResourceConfig(resourceType, draft))) return
    await onSubmit(normalizeResourceConfig(resourceType, draft))
  }

  const handleChange = (field, value) => setDraft((current) => ({ ...current, [field]: value }))

  return (
    <Dialog
      open={open}
      title="Replace connection details"
      description="Existing secret values remain hidden. Saving replaces the full connection configuration."
      onClose={onClose}
      size="large"
      footer={(
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" form={formId} loading={isSubmitting}>Replace</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate>
        <FormLayout>
          <div className={styles.modeRow}>
            <span className={styles.typeLabel}>{resourceType}</span>
            <Button
              type="button"
              variant="secondary"
              size="compact"
              onClick={() => {
                setAdvanced((value) => !value)
                setErrors({})
              }}
            >
              {advanced ? 'Use fields' : 'Advanced JSON'}
            </Button>
          </div>

          {Object.keys(errors).length > 0 ? (
            <ErrorSummary ref={errorSummaryRef}>Review the highlighted connection fields.</ErrorSummary>
          ) : null}

          {advanced ? (
            <CodeField
              label="Configuration JSON"
              value={rawConfig}
              onChange={setRawConfig}
              error={errors.raw}
              help="Authorized advanced replacement of the complete configuration object."
              rows={14}
            />
          ) : (
            <ConfigFields type={resourceType} draft={draft} errors={errors} onChange={handleChange} />
          )}
        </FormLayout>
      </form>
    </Dialog>
  )
}

function ConfigFields({ type, draft, errors, onChange }) {
  if (type === 'postgresql') {
    return (
      <>
        <FormLayoutRow columns={2}>
          <Field id="host" label="Host" value={draft.host} onChange={onChange} error={errors.host} required />
          <Field id="port" label="Port" type="number" value={draft.port} onChange={onChange} />
        </FormLayoutRow>
        <Field id="database" label="Database" value={draft.database} onChange={onChange} error={errors.database} required />
        <FormLayoutRow columns={2}>
          <Field id="username" label="Username" value={draft.username} onChange={onChange} error={errors.username} required />
          <Field id="password" label="Password" type="password" value={draft.password} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <SelectField
            id="ssl_mode"
            label="SSL Mode"
            value={draft.ssl_mode}
            onChange={onChange}
            options={[["disable", "Disable"], ["require", "Require"], ["verify-ca", "Verify CA"], ["verify-full", "Verify Full"]]}
          />
          <Field id="schema" label="Schema" value={draft.schema} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="max_connections" label="Max Connections" type="number" value={draft.max_connections} onChange={onChange} optional />
          <Field id="connect_timeout" label="Connect Timeout" type="number" value={draft.connect_timeout} onChange={onChange} optional />
        </FormLayoutRow>
      </>
    )
  }

  if (type === 'redis') {
    return (
      <>
        <FormLayoutRow columns={2}>
          <Field id="host" label="Host" value={draft.host} onChange={onChange} error={errors.host} required />
          <Field id="port" label="Port" type="number" value={draft.port} onChange={onChange} />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="password" label="Password" type="password" value={draft.password} onChange={onChange} optional />
          <Field id="database" label="Database Number" type="number" value={draft.database} onChange={onChange} />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="max_connections" label="Max Connections" type="number" value={draft.max_connections} onChange={onChange} optional />
          <Field id="connect_timeout" label="Connect Timeout" type="number" value={draft.connect_timeout} onChange={onChange} optional />
        </FormLayoutRow>
        <Checkbox label="TLS" checked={Boolean(draft.tls)} onChange={(event) => onChange('tls', event.target.checked)} />
        <Checkbox label="Cluster Mode" checked={Boolean(draft.cluster_mode)} onChange={(event) => onChange('cluster_mode', event.target.checked)} />
        <TextAreaField id="cluster_nodes" label="Cluster Nodes" language="text" rows={4} value={draft.cluster_nodes} onChange={onChange} optional />
      </>
    )
  }

  if (type === 'rest_api') {
    return (
      <>
        <Field id="base_url" label="Base URL" type="url" value={draft.base_url} onChange={onChange} error={errors.base_url} required />
        <FormLayoutRow columns={2}>
          <SelectField id="auth_type" label="Auth Type" value={draft.auth_type} onChange={onChange} options={[["", "None"], ["bearer", "Bearer"], ["basic", "Basic"], ["api_key", "API Key"], ["oauth2", "OAuth2"]]} />
          <Field id="timeout" label="Timeout" type="number" value={draft.timeout} onChange={onChange} />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="auth_token" label="Auth Token" type="password" value={draft.auth_token} onChange={onChange} optional />
          <Field id="api_key" label="API Key" type="password" value={draft.api_key} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="username" label="Username" value={draft.username} onChange={onChange} optional />
          <Field id="password" label="Password" type="password" value={draft.password} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="api_key_header" label="API Key Header" value={draft.api_key_header} onChange={onChange} optional />
          <Field id="rate_limit" label="Rate Limit" type="number" value={draft.rate_limit} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="max_retries" label="Max Retries" type="number" value={draft.max_retries} onChange={onChange} optional />
          <Field id="retry_delay" label="Retry Delay" type="number" value={draft.retry_delay} onChange={onChange} optional />
        </FormLayoutRow>
        <TextAreaField id="headers_json" label="Headers JSON" rows={5} value={draft.headers_json} onChange={onChange} error={errors.headers_json} optional />
      </>
    )
  }

  if (type === 'graphql') {
    return (
      <>
        <Field id="endpoint" label="Endpoint" type="url" value={draft.endpoint} onChange={onChange} error={errors.endpoint} required />
        <FormLayoutRow columns={2}>
          <SelectField id="auth_type" label="Auth Type" value={draft.auth_type} onChange={onChange} options={[["", "None"], ["bearer", "Bearer"], ["api_key", "API Key"]]} />
          <Field id="timeout" label="Timeout" type="number" value={draft.timeout} onChange={onChange} />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="auth_token" label="Auth Token" type="password" value={draft.auth_token} onChange={onChange} optional />
          <Field id="api_key" label="API Key" type="password" value={draft.api_key} onChange={onChange} optional />
        </FormLayoutRow>
        <FormLayoutRow columns={2}>
          <Field id="api_key_header" label="API Key Header" value={draft.api_key_header} onChange={onChange} optional />
          <Field id="rate_limit" label="Rate Limit" type="number" value={draft.rate_limit} onChange={onChange} optional />
        </FormLayoutRow>
        <Field id="max_retries" label="Max Retries" type="number" value={draft.max_retries} onChange={onChange} optional />
        <TextAreaField id="headers_json" label="Headers JSON" rows={5} value={draft.headers_json} onChange={onChange} error={errors.headers_json} optional />
      </>
    )
  }

  if (type === 'gcs') {
    return (
      <>
        <FormLayoutRow columns={2}>
          <Field id="project_id" label="Project ID" value={draft.project_id} onChange={onChange} optional />
          <Field id="bucket_name" label="Bucket Name" value={draft.bucket_name} onChange={onChange} error={errors.bucket_name} required />
        </FormLayoutRow>
        <Checkbox label="Use Default Credentials" checked={Boolean(draft.use_default_creds)} onChange={(event) => onChange('use_default_creds', event.target.checked)} />
        <Field id="storage_emulator_host" label="Storage Emulator Host" value={draft.storage_emulator_host} onChange={onChange} optional />
        <TextAreaField id="service_account_json" label="Service Account JSON" rows={8} value={draft.service_account_json} onChange={onChange} optional />
      </>
    )
  }

  return <p className={styles.note}>This resource type cannot be edited from Studio.</p>
}

function Field({ id, label, value, onChange, type = 'text', error, ...props }) {
  return (
    <FormField label={label} error={error} {...props}>
      <Input type={type} value={value ?? ''} onChange={(event) => onChange(id, event.target.value)} />
    </FormField>
  )
}

function TextAreaField({ id, label, value, onChange, rows, error, ...props }) {
  return (
    <CodeField
      id={id}
      label={label}
      rows={rows}
      value={value ?? ''}
      onChange={(nextValue) => onChange(id, nextValue)}
      error={error}
      {...props}
    />
  )
}

function SelectField({ id, label, value, onChange, options }) {
  return (
    <FormField label={label}>
      <Select value={value ?? ''} onChange={(event) => onChange(id, event.target.value)}>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </Select>
    </FormField>
  )
}
